import { useEffect, useRef, useState } from "react";
import { useToast } from "../../hooks/use-toast";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import type { Entity, Pincode } from "../../lib/types";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface PincodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: Entity | null;
  onSave: () => void;
}

export const PincodeDialog = ({
  open,
  onOpenChange,
  entity,
  onSave,
}: PincodeDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPincodes, setSelectedPincodes] = useState<Pincode[]>([]);
  const [availablePincodes, setAvailablePincodes] = useState<Pincode[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [bulkUploadText, setBulkUploadText] = useState("");
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkDeleteText, setBulkDeleteText] = useState("");
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [initialPincodes, setInitialPincodes] = useState<Pincode[]>([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { accessLevel } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (entity && open) {
      fetchEntityPincodes();
      fetchAvailablePincodes();
    }
  }, [entity, open]);

  const fetchEntityPincodes = async () => {
    if (!entity) return;
    try {
      const response = await api.getEntityPincodes(entity.id || 0);
      setSelectedPincodes(response.results);
      setInitialPincodes(response.results);
    } catch (error) {
      console.error("Failed to fetch entity pincodes:", error);
      toast({
        title: "Failed to fetch entity pincodes",
        description: `${error}`,
        variant: "destructive",
      });
    }
  };

  const fetchAvailablePincodes = async () => {
    if (searchTerm.length < 3) return;
    try {
      const response = await api.searchPincodes(
        searchTerm,
        Object.entries(accessLevel?.access_level || {})
          .map(([key, values]) => `${key}:${values.join(",")}`)
          .join("|")
      );
      setAvailablePincodes(response.results);
    } catch (error) {
      console.error("Failed to fetch pincodes:", error);
      toast({
        title: "Failed to fetch pincodes",
        description: `${error}`,
        variant: "destructive",
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchAvailablePincodes is not a dependency
  useEffect(() => {
    if (searchTerm.length >= 3) {
      fetchAvailablePincodes();
    } else {
      setAvailablePincodes([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (availablePincodes.length > 0) {
      setPopoverOpen(true);
      inputRef.current?.focus();
    } else {
      setPopoverOpen(false);
    }
  }, [availablePincodes]);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSelectedPincodes([]);
      setInitialPincodes([]);
      setShowBulkUpload(false);
      setBulkUploadText("");
      setShowBulkDelete(false);
      setBulkDeleteText("");
      setShowSaveConfirmation(false);
    }
  }, [open]);

  const executeSave = async () => {
    if (!entity) return;
    setIsLoading(true);
    try {
      const pincodesToAdd = selectedPincodes.filter(p => !initialPincodes.find(ip => ip.id === p.id));
      const pincodesToRemoveIds = initialPincodes
        .filter(p => !selectedPincodes.find(sp => sp.id === p.id))
        .map(p => p.id);

      const promises = [];

      if (pincodesToRemoveIds.length > 0) {
        promises.push(api.bulkDeleteEntityPincodes(entity.id || 0, pincodesToRemoveIds));
      }

      if (pincodesToAdd.length > 0) {
        const pincodesToAddStrings = pincodesToAdd.map(p => p.pincode.toString());
        promises.push(api.bulkUploadEntityPincodes(entity.id || 0, pincodesToAddStrings));
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      } else {
        toast({
          title: "No Changes",
          description: "There were no new pincodes to add or remove.",
        });
        setShowSaveConfirmation(false);
        setIsLoading(false);
        return;
      }

      if (onSave) {
        onSave();
      }

      setShowSaveConfirmation(false);
      onOpenChange(false);
      toast({
        title: "Pincodes Updated",
        description: `${entity.name} pincodes updated successfully.`,
      });
    } catch (error) {
      console.error("Failed to update entity pincodes:", error);
      toast({
        title: "Failed to update entity pincodes",
        description: `${error}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    if (!entity) return;

    const pincodesToRemove = initialPincodes.filter(p => !selectedPincodes.find(sp => sp.id === p.id));
    if (pincodesToRemove.length > 0) {
      setShowSaveConfirmation(true);
      return;
    }
    executeSave();
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const popover = popoverRef.current;
    if (popover && !popover.contains(event.target as Node)) {
      setPopoverOpen(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleOutsideClick is not a dependency
  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handlePincodeSelect = (pincode: Pincode) => {
    if (!selectedPincodes.find((p) => p.id === pincode.id)) {
      setSelectedPincodes([...selectedPincodes, pincode]);
      setAvailablePincodes(
        availablePincodes.filter((p) => p.id !== pincode.id)
      );
    } else {
      toast({
        title: "Pincode already added to entity",
        description: "Selected pincode is already mapped to the entity",
        variant: "destructive",
      });
    }
    setPopoverOpen(false);
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPincodes = selectedPincodes.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(selectedPincodes.length / itemsPerPage);

  // Handle page changes
  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleBulkUpload = async () => {
    if (!entity || !bulkUploadText.trim()) return;

    setIsLoading(true);
    try {
      const pincodeList = bulkUploadText
        .split(/[\s,]+/)
        .map((pincode) => pincode.trim())
        .filter((pincode) => /^\d{6}$/.test(pincode));

      const accessLevelString = Object.entries(accessLevel?.access_level || {})
        .map(([key, values]) => `${key}:${values.join(",")}`)
        .join("|");

      const newPincodes: Pincode[] = [];
      const failedPincodes: string[] = [];

      for (const pincodeStr of pincodeList) {
        if (selectedPincodes.some(p => p.pincode.toString() === pincodeStr)) {
          continue;
        }
        try {
          const response = await api.searchPincodes(pincodeStr, accessLevelString);
          if (response.results && response.results.length > 0) {
            const foundPincode = response.results.find((p: Pincode) => p.pincode.toString() === pincodeStr);
            if (foundPincode) {
              newPincodes.push(foundPincode);
            } else {
              failedPincodes.push(pincodeStr);
            }
          } else {
            failedPincodes.push(pincodeStr);
          }
        } catch (error) {
          failedPincodes.push(pincodeStr);
          console.error(`Failed to fetch pincode ${pincodeStr}:`, error);
        }
      }

      if (newPincodes.length > 0) {
        setSelectedPincodes(prev => [...prev, ...newPincodes]);
        toast({
          title: "Pincodes Added",
          description: `${newPincodes.length} new pincodes added to the list. Click 'Save Changes' to persist.`,
        });
      }

      if (failedPincodes.length > 0) {
        toast({
          title: "Some Pincodes Not Found",
          description: `Could not find or add: ${failedPincodes.join(", ")}. They may be invalid or already added.`,
          variant: "destructive",
        });
      }

      setBulkUploadText("");
      setShowBulkUpload(false);
    } catch (error) {
      console.error("Failed to bulk add pincodes:", error);
      toast({
        title: "Failed to bulk add pincodes",
        description: `${error}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkDelete = () => {
    if (!bulkDeleteText.trim()) return;

    const pincodesToDelete = bulkDeleteText
      .split(/[\s,]+/)
      .map((pincode) => pincode.trim())
      .filter((pincode) => /^\d{6}$/.test(pincode));

    const originalCount = selectedPincodes.length;
    const newSelectedPincodes = selectedPincodes.filter(
      (p) => !pincodesToDelete.includes(p.pincode.toString())
    );
    const removedCount = originalCount - newSelectedPincodes.length;

    if (removedCount > 0) {
        setSelectedPincodes(newSelectedPincodes);
        toast({
          title: "Pincodes Marked for Deletion",
          description: `${removedCount} pincodes will be removed on save.`,
        });
    } else {
        toast({
          title: "No matching pincodes found",
          description: "None of the provided pincodes were in the selected list.",
          variant: "destructive"
        });
    }

    setBulkDeleteText("");
    setShowBulkDelete(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Manage Pincodes - {entity?.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">Selected Pincodes:</h4>
            <div className="flex gap-1 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowBulkUpload(true);
                  setShowBulkDelete(false);
                  setBulkDeleteText("");
                }}
                disabled={isLoading}
                className="whitespace-nowrap text-xs px-2 py-1"
              >
                Bulk Upload
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowBulkDelete(true);
                  setShowBulkUpload(false);
                  setBulkUploadText("");
                }}
                disabled={isLoading}
                className="whitespace-nowrap text-xs px-2 py-1"
              >
                Bulk Delete
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedPincodes([])}
                className="whitespace-nowrap text-xs px-2 py-1"
              >
                Clear All
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {currentPincodes.map((pincode) => (
              <div
                key={pincode.id}
                className="flex items-center gap-2 bg-secondary p-2 rounded-md"
              >
                {pincode.pincode}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPincodes(
                      selectedPincodes.filter((p) => p.id !== pincode.id)
                    );
                  }}
                  className="text-destructive ml-2"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {selectedPincodes.length > itemsPerPage && (
            <div className="flex justify-between items-center mt-2">
              <div className="text-sm text-muted-foreground">
                Showing {indexOfFirstItem + 1}-
                {Math.min(indexOfLastItem, selectedPincodes.length)} of{" "}
                {selectedPincodes.length}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {!showBulkUpload && !showBulkDelete && (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger className="w-full">
              <Input
                ref={inputRef}
                placeholder="Search pincodes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="focus:visible-ring-0"
              />
            </PopoverTrigger>
            <div ref={popoverRef}>
              <PopoverContent className="dark:bg-gray-800 dark:border-gray-700">
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {availablePincodes.length === 0 ? (
                    <div>No results found.</div>
                  ) : (
                    <ul>
                      {availablePincodes?.map((availablePincode) => (
                        <li
                          key={availablePincode.id}
                          onClick={() => handlePincodeSelect(availablePincode)}
                          onKeyUp={(e) =>
                            e.key === "Enter" &&
                            handlePincodeSelect(availablePincode)
                          }
                          className="cursor-pointer p-2 hover:bg-gray-200"
                        >
                          {availablePincode.pincode}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </PopoverContent>
            </div>
          </Popover>
        )}

        {showBulkUpload && (
          <div className="space-y-2 p-4 border rounded-md">
            <h4 className="font-medium">Bulk Upload Pincodes</h4>
            <p className="text-sm text-muted-foreground">
              Enter pincodes:
            </p>
            <textarea
              value={bulkUploadText}
              onChange={(e) => setBulkUploadText(e.target.value)}
              placeholder="Enter pincodes separated by comma or space"
              className="w-full h-32 p-2 border rounded-md resize-none text-black"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowBulkUpload(false);
                  setBulkUploadText("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleBulkUpload}
                disabled={isLoading || !bulkUploadText.trim()}
              >
                Add to list
              </Button>
            </div>
          </div>
        )}

        {showBulkDelete && (
          <div className="space-y-2 p-4 border rounded-md">
            <h4 className="font-medium">Bulk Delete Pincodes</h4>
            <p className="text-sm text-muted-foreground">
              Enter pincodes:
            </p>
            <textarea
              value={bulkDeleteText}
              onChange={(e) => {
                const numbersOnly = e.target.value.replace(/[^\d,]/g, '');
                const formatted = numbersOnly.replace(/(\d{6})(?=\d)/g, '$1,');
                setBulkDeleteText(formatted);
              }}
              placeholder="Enter pincodes to delete"
              className="w-full h-32 p-2 border rounded-md resize-none text-black"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowBulkDelete(false);
                  setBulkDeleteText("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={isLoading || !bulkDeleteText.trim()}
              >
                Remove from list
              </Button>
            </div>
          </div>
        )}

        {showSaveConfirmation && (
          <div className="space-y-2 p-4 border rounded-md bg-red-50">
            <h4 className="font-medium text-red-800">Confirm Pincode Deletion</h4>
            <p className="text-sm text-red-700">
              You have removed pincodes from this entity. This action cannot be undone. Are you sure you want to save these changes?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSaveConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={executeSave}
                disabled={isLoading}
              >
                Yes, Save and Delete
              </Button>
            </div>
          </div>
        )}

        <Button onClick={handleSave} disabled={showSaveConfirmation || isLoading}>Save Changes</Button>
      </DialogContent>
    </Dialog>
  );
};
